export interface SyncChannelRegistration {
  appId: string;
  entityType: string;
  schemaVersion: number;
  sourceApp: string;
  scope: "personal";
  visibility: "private";
  capability: "";
  permission: "owner";
}

export const SYNC_STORAGE_APP_IDS = [
  "arcade",
  "autobot",
  "compass",
  "couple",
  "echo",
  "fortuna",
  "helmwise",
  "jarvis",
  "lightyear",
  "portal",
  "puzzle",
  "quill",
  "steward",
] as const;

const channelsByProduct = {
  arcade: { schemaVersion: 24, entityTypes: ["plugin-install-states", "extension-install-states"] },
  autobot: { schemaVersion: 21, entityTypes: ["record"] },
  compass: { schemaVersion: 23, entityTypes: ["saved-station", "station-feedback"] },
  couple: { schemaVersion: 22, entityTypes: ["record", "profile"] },
  echo: { schemaVersion: 9, entityTypes: ["workspace"] },
  fortuna: {
    schemaVersion: 20,
    entityTypes: [
      "asset",
      "cash_balance",
      "history_snapshot",
      "news_item",
      "position",
      "quote_snapshot",
      "search_history",
      "settings",
      "watchlist-group-manager",
      "watchlist_group",
      "watchlist_item",
      "ledger-transaction",
      "ledger-price",
      "ledger-fx",
      "ledger-import",
    ],
  },
  helmwise: { schemaVersion: 7, entityTypes: ["workspace", "calendar-projection"] },
  jarvis: { schemaVersion: 22, entityTypes: ["workspace", "protected-chunk"] },
  lightyear: {
    schemaVersion: 6,
    entityTypes: [
      "calendar",
      "event",
      "focus-session",
      "habit",
      "habit-record",
      "planner-document",
      "project-workspace",
      "reminder",
      "scheduling-policy",
      "task",
      "task-list",
    ],
  },
  puzzle: { schemaVersion: 10, entityTypes: ["history"] },
  steward: {
    schemaVersion: 22,
    entityTypes: ["room-history", "subscription_payment", "subscription_plan", "utility-preferences", "calendar-projection"],
  },
} as const;

export const SYNC_CHANNEL_REGISTRY: readonly SyncChannelRegistration[] =
  Object.freeze(
    Object.entries(channelsByProduct).flatMap(([appId, registration]) =>
      registration.entityTypes.map((entityType) =>
        Object.freeze({
          appId,
          entityType,
          schemaVersion: registration.schemaVersion,
          sourceApp: appId,
          scope: "personal" as const,
          visibility: "private" as const,
          capability: "" as const,
          permission: "owner" as const,
        }),
      ),
    ),
  );

const channelByIdentity = new Map(
  SYNC_CHANNEL_REGISTRY.map((channel) => [
    JSON.stringify([channel.appId, channel.entityType]),
    channel,
  ]),
);

export function resolveSyncChannelRegistration(
  appId: string,
  entityType: string,
): SyncChannelRegistration | null {
  return channelByIdentity.get(JSON.stringify([appId, entityType])) ?? null;
}

export function assertSyncChannelRegistration(
  appId: string,
  entityType: string,
  schemaVersion: number,
): SyncChannelRegistration {
  const channel = resolveSyncChannelRegistration(appId, entityType);
  if (!channel || channel.schemaVersion !== schemaVersion) {
    throw new Error(
      `Unknown or incompatible sync channel: ${appId}/${entityType}@${schemaVersion}`,
    );
  }
  return channel;
}
